import { useForm } from 'react-hook-form';
import {
  defaultSocItemValues,
  SocItemDetailsOfqual,
} from '../../domain/SocItemDetailsOfqual';
import { NonNullableItemState } from '../../redux/state';
import { ModuleAppBar } from '../ModuleAppBar';
import { ItemDetails } from './ItemDetails';
import { ItemDetailsOfqual } from './ItemDetailsOfqual';
import { ItemTabs } from './ItemTabs';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '@ofqual/portal-core/core/hooks';
import { getLookupTracker } from '@ofqual/portal-core/core/utils/modelHelpers';
import {
  aoItemDetailSelector,
  authSelector,
  ofqualItemDetailSelector,
} from '../../redux/selectors';
import { SectionProps } from '../sections/SectionProps';
import { SocStatus, RegulatorsToNotifyModel } from '../../domain/SocItem';
import { useDispatch, useSelector } from 'react-redux';
import { AppBarSaveButton } from '@ofqual/portal-components/components/SecondaryAppBar/AppBarSaveButton';
import { UserType } from '../../redux/types';
import { submitSocAction, enableResubmission, updatingOfqualItem, updatingAoItem, updatingRegulatorsToNotify, reopenSoc } from '../../redux/actions';
import {
  defaultSocItemValuesAo,
  SocItemDetailsAo,
  requiredOfqualFields
} from '../../domain/SocItemDetailsAo';
import { AppBarButton, AppBarValidationIndicator, AppBarConfirmButton } from '@ofqual/portal-components/components/SecondaryAppBar';
import { LockOpen, Restore, Send } from '@material-ui/icons';
import { SocSubmitStatus } from '../../domain/SocSubmitStatus';
import { CloseButtonAndDialog} from './dialog/CloseDialog';
import { Prompt } from 'react-router-dom';
import { validateAttachments, validateCurrencyFields } from '../utils/itemFormValidation';

export const ItemForm: React.FC<{
  state: NonNullableItemState;
}> = ({ state }) => {
  var socItem = state.item;

  const dispatch = useDispatch();
  const { userType, token } = useAuth(authSelector);
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  // Show save button or not
  const isClosed = state.item.status === SocStatus.Closed;
  const aoDisabled =
    userType === UserType.AO &&
    state.item.status !== SocStatus.Draft &&
    state.item.status !== SocStatus.New;
  const ofqualDisabled =
    userType === UserType.Ofqual && isClosed;
  const disabled = aoDisabled || ofqualDisabled;
  
  const canClose = userType === UserType.Ofqual && !isClosed;

  // Merge the state with defaults
  var ofqualItemState = useSelector(ofqualItemDetailSelector);
  var itemDetailsOfqual = { ...defaultSocItemValues };
  if (ofqualItemState.status === 'LOADED') {
    itemDetailsOfqual = {
      ...itemDetailsOfqual,
      ...ofqualItemState?.itemOfqualDetails,
    };
  }

  const onEnableResubmission = (id: string, year: string) =>
    dispatch(enableResubmission(id, year));

  const onReopen = (id: string, year: string) => 
    dispatch(reopenSoc(state.item.id, state.item.year));

  // Create ofqual form
  const ofqualItemDetails = useForm<SocItemDetailsOfqual>({
    defaultValues: itemDetailsOfqual,
    shouldUnregister: false,
  });

  // Create ofqual section props
  const sectionPropsOfqual: SectionProps<SocItemDetailsOfqual> = {
    isCreateMode: false,
    disabled: disabled,
    user: { userType, token },
    control: ofqualItemDetails.control,
    errors: ofqualItemDetails.errors,
    formState: ofqualItemDetails.formState,
    model: itemDetailsOfqual,
    clearErrors: ofqualItemDetails.clearErrors,
    setError: ofqualItemDetails.setError,
    setValue: (name, value) => ofqualItemDetails.setValue(name, value),
    getValues: ofqualItemDetails.getValues,
    registerCustomError: (_name, _isError) => {},
    lookupTracker: lookupTracker,
    watch: ofqualItemDetails.watch,
    trigger: ofqualItemDetails.trigger
  };

  // Retrieve AO item details
  var aoItemState = useSelector(aoItemDetailSelector);
  var itemDetailsAo = { ...defaultSocItemValuesAo };

  // Merge the state with defaults
  if (aoItemState.status === 'LOADED') {
    itemDetailsAo = { ...itemDetailsAo, ...aoItemState?.itemAoDetails };
  }

  // Create AO form
  const aoItemDetails = useForm<SocItemDetailsAo>({
    defaultValues: itemDetailsAo,
    shouldUnregister: false,
  });

  // Create AO section props
  const sectionPropsAo: SectionProps<SocItemDetailsAo> = {
    isCreateMode: false,
    disabled:disabled,
    user: { userType, token },
    control: aoItemDetails.control,
    errors: aoItemDetails.errors,
    formState: aoItemDetails.formState,
    model: itemDetailsAo,
    clearErrors: aoItemDetails.clearErrors,
    setError: aoItemDetails.setError,
    setValue: (name, value) => aoItemDetails.setValue(name, value),
    getValues: aoItemDetails.getValues,
    registerCustomError: (_name, _isError) => {},
    lookupTracker: lookupTracker,
    watch: aoItemDetails.watch,
    trigger: aoItemDetails.trigger
  };

  // Create RegulatorToNotify form
  const regulatorsToNotifyItemDetails = useForm<RegulatorsToNotifyModel>({
    defaultValues: socItem,
    shouldUnregister: false,
  });

  // create RegulatorToNotify section props
  const regulatorsToNotifySectionPropsItem: SectionProps<RegulatorsToNotifyModel> = {
    isCreateMode: false,
    disabled: disabled,
    user: { userType, token },
    control: regulatorsToNotifyItemDetails.control,
    errors: regulatorsToNotifyItemDetails.errors,
    formState: regulatorsToNotifyItemDetails.formState,
    model: socItem,
    clearErrors: regulatorsToNotifyItemDetails.clearErrors,
    setError: regulatorsToNotifyItemDetails.setError,
    setValue: (name, value) => regulatorsToNotifyItemDetails.setValue(name, value),
    getValues: regulatorsToNotifyItemDetails.getValues,
    registerCustomError: (_name, _isError) => { },
    lookupTracker: lookupTracker,
    watch: regulatorsToNotifyItemDetails.watch,
    trigger: regulatorsToNotifyItemDetails.trigger
  };

  // This keeps the controls version of data the same as the states
   const resetValues = useCallback((userType :string) => {
    if (userType === 'ao' && aoItemState.itemAoDetails) {
      aoItemDetails.reset(aoItemState.itemAoDetails);
    } else if (userType === 'ofqual' && ofqualItemState.itemOfqualDetails) {
      ofqualItemDetails.reset(ofqualItemState.itemOfqualDetails);
    }
    // eslint-disable-next-line
  },[ aoItemDetails.reset, ofqualItemDetails.reset, aoItemState.itemAoDetails, ofqualItemState.itemOfqualDetails ]);
  
  useEffect(() => {      

    if (ofqualItemState.status === 'LOADED') {
      resetValues('ofqual');
    }
    if (aoItemState.status === 'LOADED') {
      resetValues('ao');
    }
  }, [ aoItemState.status, ofqualItemState.status, resetValues ]);
  
  // Handle on submit
  const onSubmit = async (saveAction: SocSubmitStatus) => {
    const data: SocItemDetailsOfqual = ofqualItemDetails.getValues();
    const dataAo: SocItemDetailsAo = aoItemDetails.getValues();
    const dataReg: RegulatorsToNotifyModel = regulatorsToNotifyItemDetails.getValues();
   
    const attachments = [
      { key: 'accountStatementAttachment', file: dataAo.accountStatementAttachment },
      { key: 'kloe1Attachment', file: dataAo.kloe1Attachment },
      { key: 'kloe2Attachment', file: dataAo.kloe2Attachment },
      { key: 'kloe3Attachment', file: dataAo.kloe3Attachment },
    ];

    const currencyKeys: (keyof SocItemDetailsAo)[] = [
      'totalRevenue',
      'totalExpenditure',
      'cashInHand',
      'annualFixedCosts',
    ];

    const negativeCurrencyKeys: (keyof SocItemDetailsAo)[] = [
      'netProfit'
    ];

    // Validate attachments, currency fields and negative currency fields
    const attachmentsHasError = await validateAttachments(attachments, saveAction, aoItemDetails);
    const currencyHasError = await validateCurrencyFields(currencyKeys, aoItemDetails);
    const negativeCurrencyHasError = await validateCurrencyFields(negativeCurrencyKeys, aoItemDetails, true);

    if (attachmentsHasError ||currencyHasError || negativeCurrencyHasError) return;

    // Ao save
    if (userType === UserType.AO) {
      if (saveAction === SocSubmitStatus.Save) {
        dispatch(updatingAoItem(state.item.year, dataAo));
        // RegulatorsToNotify save
        dispatch(updatingRegulatorsToNotify(state.item.id, state.item.year, dataReg),
        );
      }
      else {
        dispatch(submitSocAction(state.item.year, dataAo, dataReg, saveAction))
      }

      // Avoid resetting when saving with errors to prevent losing them
      if (saveAction !== SocSubmitStatus.SaveSubmitFail) {
        aoItemDetails.reset(dataAo);
        regulatorsToNotifyItemDetails.reset(dataReg);
      }
    }
    // Ofqual save
    if (userType === UserType.Ofqual) {
      dispatch(updatingOfqualItem(state.item.year, data));
      ofqualItemDetails.reset(data);
    }
  };

  const canSend = userType === UserType.AO && (state.item.status === SocStatus.Draft || state.item.status === SocStatus.New);

  const onSave = () => {
    // Clears all errors when the user attempts to save with existing errors
    aoItemDetails.clearErrors(requiredOfqualFields)
    regulatorsToNotifyItemDetails.clearErrors()

    onSubmit(SocSubmitStatus.Save)
  }

  const onSend = async (event: any) => {
    if (event) {
      // Prepare some temp variables for saving
      let aoResult = false
      let regResult = false

      // Prepare the promise references, with callbacks for updating the results as appropriate
      let aoVerify = aoItemDetails.handleSubmit(
        () => { aoResult = true; }, 
        () => { aoResult = false; } 
      )
      let regVerify = regulatorsToNotifyItemDetails.handleSubmit(
        () => { regResult = true; }, 
        () => { regResult = false; } 
      )

      // Call the promises
      await aoVerify()
      await regVerify()

      // Check the results, either save, or save and submit
      if (aoResult && regResult) {
        onSubmit(SocSubmitStatus.SaveSubmitPass)
      } else {
        onSubmit(SocSubmitStatus.SaveSubmitFail)
      }
    }
  }

  const IsOfqual = userType === UserType.Ofqual;
  const canEnableResubmission = IsOfqual && state.item.status === SocStatus.Submitted; 
  const canReopen = IsOfqual && state.item.status === SocStatus.Closed;
  
  
  return (
    <>
      <Prompt
          message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
          when={Object.keys(aoItemDetails.formState.dirtyFields).length > 0 || 
                Object.keys(ofqualItemDetails.formState.dirtyFields).length > 0 ||
                Object.keys(regulatorsToNotifyItemDetails.formState.dirtyFields).length > 0
          }
      />
      <ModuleAppBar breadcrumbs={[{ label: `${socItem.caseTitle}` }]}>
        <AppBarValidationIndicator errors={sectionPropsAo.errors} />
        {canReopen ? (<AppBarConfirmButton
                Icon = { Restore }
                text="Reopen"
                tooltip="Reopen SoC"
                onClick={ () => onReopen(state.item.id, state.item.year) }
                data-test="reopen-soc-button"                
            >  
              Are you sure you want to reopen this SoC?
            </AppBarConfirmButton>) : null }       
        {canClose ? (<CloseButtonAndDialog
          id={state.item.id}
          year={state.item.year}
        />
        ) : null}
        <AppBarSaveButton
          onClick={onSave}
          isSubmitting={ofqualItemState.status === 'UPDATING' || aoItemState.status === 'UPDATING' || state.status === 'UPDATING'}
          isSubmitted={ofqualItemState.lastCrudResult.isSuccess || aoItemState.lastCrudResult.isSuccess || state.lastCrudResult.isSuccess}
          dataTest="save-soc-button"
          disabled={disabled}
        />
        {canSend ? (
          <>
            <AppBarButton
              onClick={onSend}                   
              color="primary"
              text="Send"
              Icon={Send}
              data-test="send-button"
            />
          </>
        ) : (
          false
        )}

        {canEnableResubmission ? (
          <AppBarConfirmButton
            dataTest="allow-ao-resubmission"
            Icon={LockOpen}
            onClick={() => onEnableResubmission(state.item.id, state.item.year)}
            text="Allow Resubmission"
            tooltip="Allow AO resubmission"
          >
            Are you sure you want to allow the AO to resubmit this SoC?
          </AppBarConfirmButton>
        ) : null}        

      </ModuleAppBar>

      <ItemTabs>
        {(activeTab) =>
          activeTab === 'ofqual' ? (
            <ItemDetailsOfqual sectionPropsOfqual={sectionPropsOfqual} />
          ) : (
            <ItemDetails
              sectionPropsAO={sectionPropsAo}
              sectionPropsReg={regulatorsToNotifySectionPropsItem}
            />
          )
        }
      </ItemTabs>
    </>
  );
};